// Niveaux d'urgence d'une échéance (séance à couvrir, échéance de suivi…),
// calculés sur des jours calendaires restants avant la date cible. Seuils
// configurables via app_settings (calendrier.urgence_*), lus par l'appelant.
// Partagé entre TableauCalendrier (trous de couverture) et TableauPage
// (demandes non résolues) — une seule définition, pour que les deux vues
// s'accordent.

import type { Statut } from '../../types';

/** Pas encore réglées : seules elles portent une urgence (une demande confirmée, clôturée ou refusée n'a plus d'échéance qui compte). */
export const STATUTS_NON_RESOLUS: Statut[] = [
  'brouillon', 'soumise', 'recherche_interne_1', 'recherche_interne_2', 'recherche_externe',
];

export type NiveauUrgence = 'urgent' | 'presse' | 'alancer' | 'anticipe' | 'depasse';

export interface SeuilsUrgence { urgent: number; presse: number; alancer: number; }

export const SEUILS_URGENCE_PAR_DEFAUT: SeuilsUrgence = { urgent: 7, presse: 18, alancer: 30 };

/** Jours calendaires entre aujourd'hui et la date cible (négatif = dépassé). */
export function joursAvant(dateISO: string, aujourdhui: Date): number {
  const [y, m, j] = dateISO.split('-').map(Number);
  const cible = new Date(y, m - 1, j);
  const debutJour = new Date(aujourdhui.getFullYear(), aujourdhui.getMonth(), aujourdhui.getDate());
  return Math.round((cible.getTime() - debutJour.getTime()) / 86_400_000);
}

export function niveauUrgence(joursRestants: number, seuils: SeuilsUrgence): NiveauUrgence {
  if (joursRestants < 0) return 'depasse';
  if (joursRestants <= seuils.urgent) return 'urgent';
  if (joursRestants <= seuils.presse) return 'presse';
  if (joursRestants <= seuils.alancer) return 'alancer';
  return 'anticipe';
}

export const NIVEAU_URGENCE_INFO: Record<NiveauUrgence, { label: string; badgeClass: string }> = {
  urgent: { label: 'Urgent', badgeClass: 'badge badge-err' },
  presse: { label: 'Pressé', badgeClass: 'badge badge-warn' },
  alancer: { label: 'À lancer', badgeClass: 'badge badge-accent' },
  anticipe: { label: 'Anticipé', badgeClass: 'badge' },
  depasse: { label: 'Dépassé', badgeClass: 'badge' },
};

export function libelleEcheance(joursRestants: number): string {
  return joursRestants >= 0 ? `J-${joursRestants}` : `+${-joursRestants}j`;
}

/** Seuils depuis /api/settings (déjà accessible à tout compte authentifié). */
export function seuilsDepuisSettings(s: Record<string, string>): SeuilsUrgence {
  return {
    urgent: Number(s['calendrier.urgence_urgent_max_jours']) || SEUILS_URGENCE_PAR_DEFAUT.urgent,
    presse: Number(s['calendrier.urgence_presse_max_jours']) || SEUILS_URGENCE_PAR_DEFAUT.presse,
    alancer: Number(s['calendrier.urgence_alancer_max_jours']) || SEUILS_URGENCE_PAR_DEFAUT.alancer,
  };
}
